import { computed } from 'vue';
import { useRoute, useRouter } from '#imports';
import { isBundledNativeOrigin, isNativeApp } from '~/functions/appOrigin';
import { APP_WELCOME_PATH } from '~/functions/resolveLoginEntryPath';
import { LmsRouteNames } from '~/types/types';

/**
 * Back navigation for the packaged app.
 *
 * The app has no way back. A browser gives you three (button, keyboard, gesture) and a phone
 * gives you none of them: **iOS has no hardware back button at all**, WKWebView's edge-swipe
 * (`allowsBackForwardNavigationGestures`) defaults to `false`, Capacitor never enables it, and
 * Capacitor exposes no configuration option for it — the full `ios` option list in
 * `@capacitor/cli/dist/declarations.d.ts` has `scrollEnabled`, `zoomEnabled` and
 * `allowsLinkPreview`, but nothing for back/forward. So on iPhone the only route out of a
 * lesson is the hamburger menu, which the evaluator did not find and should not have to.
 *
 * Android is *not* in the same position, which is worth knowing before anyone "fixes" it
 * there too. `@capacitor/app`'s `AppPlugin.java` registers an `OnBackPressedCallback` that,
 * when no JS `backButton` listener exists, calls `webView.goBack()` if the WebView can go
 * back. Vue Router's `pushState` entries are ordinary WebView history, so the hardware button
 * already works. **Registering a `backButton` listener would replace that behaviour
 * wholesale**, so we deliberately do not: this composable adds a visible control and leaves
 * the hardware button alone.
 *
 * A visible control is also the better answer on its own terms. The evaluator's words were
 * "won't let me go back" — an invisible gesture does not answer that, and a chevron in the
 * top-left is what an iOS user reaches for anyway.
 */
/**
 * Whether the router has somewhere to go back to.
 *
 * Vue Router writes `back` into history state on every navigation: the previous location, or
 * null at the bottom of the stack. It is the same value `router.back()` acts on, so asking it
 * is the one check that cannot disagree with what back navigation will actually do.
 *
 * `window.history.length` is not usable here — it counts the whole tab's history, including
 * entries from before the app booted, and never decreases.
 *
 * Shared with `plugins/nativeBackButton.client.ts` so the header control and Android's
 * hardware button cannot answer this question differently.
 */
export function hasPreviousHistoryEntry(): boolean {
   if (typeof window === 'undefined') return false;

   return Boolean(window.history.state?.back);
}

/**
 * Whether the router has somewhere to go *forward* to — set only after going back, and
 * cleared the moment a new navigation is pushed, exactly as a browser's forward button
 * behaves.
 */
export function hasForwardHistoryEntry(): boolean {
   if (typeof window === 'undefined') return false;

   return Boolean(window.history.state?.forward);
}

export function useNativeBackNavigation() {
   const route = useRoute();
   const router = useRouter();

   /** True only for the packaged app serving its own bundled assets — never on the web. */
   const isPackagedApp = computed(() => isNativeApp() && isBundledNativeOrigin());

   /**
    * Routes that are the bottom of the stack. Going back from the LMS home would land on the
    * pre-login welcome screen, which the auth middleware immediately bounces an authenticated
    * user off — so the button would appear to do nothing, which is worse than not offering it.
    */
   const isRootRoute = computed(
      () => route.path === APP_WELCOME_PATH || route.name === LmsRouteNames.home
   );

   const canGoBack = computed(() => {
      // Read fullPath so this recomputes on every navigation. `history.state` is a plain
      // object that Vue does not track, so without this the value would be whatever it was
      // when the nav first rendered — and the nav is mounted once, in the layout.
      void route.fullPath;

      if (!isPackagedApp.value || isRootRoute.value) return false;

      return hasPreviousHistoryEntry();
   });

   /**
    * Forward is deliberately **not** gated on `isRootRoute`, unlike back. Going back to the
    * LMS home from a lesson is ordinary, and forward has to be able to return you to it —
    * that is the whole case the control exists for.
    *
    * It appears only when there is genuinely somewhere forward to go, which is what keeps a
    * forward affordance from reading as "web page in a shell": during ordinary
    * forward-only browsing it never renders at all, and it disappears again the moment a new
    * route is pushed. A permanently visible forward button would be the thing to avoid.
    */
   const canGoForward = computed(() => {
      void route.fullPath;

      if (!isPackagedApp.value) return false;

      return hasForwardHistoryEntry();
   });

   const goBack = () => {
      router.back();
   };

   const goForward = () => {
      router.forward();
   };

   return { canGoBack, canGoForward, goBack, goForward, isPackagedApp, isRootRoute };
}
